import { Cartesian3, Color, createGuid } from "@cesium/engine";
import Utils from "../basic/Utils";
import DitapViewer from "../basic/DitapViewer";

/**
 * 직선 거리 계산 및 영역 표시 클래스.<br>
 * Straight distance calculation and area display class.
 *
 * @alias MeasurementLengthStraight
 * @constructor
 *
 * @param {object} options 아래 속성 포함 객체.</br>Object with the following properties:
 * @param {DitapViewer} options.viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {Cartesian3[]} [options.positions]
 * 직선 거리 측정을 위한 Cartesian 좌표 배열. 지정한 경우 입력 값으로 직선 거리 계산 및 영역 표시되어 수정 불가.</br> Cartesian coordinate array for measuring straight distances. If specified, the input value is used for calculating the straight distance and displayed in the area, making it unmodifiable.
 * @param {String} [options.labelOption = "total"]
 * 직선 거리 측정 완료 후 라벨 표시 옵션. 지정하지 않은 경우 개별 표시. </br>
 * "total" : 폴리라인 전체 길이 표시. </br>
 * "single" : 폴리라인 개별 길이 표시. </br></br>
 *
 * Label display option after completing the straight distance measurement. If not specified, display individually.</br>
 * "total" : Labels displaying the full length of the polyline.</br>
 * "single" : Labels showing the length for each polyline.
 * @param {Color} [options.entityColor = Color.fromBytes(169, 67, 30, 255)] 엔티티 색상.<br> Color of the entity.
 *
 * @example
 * // 샌드캐슬 예제 'DiTAP - Straight Length Measurement' 참조
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * // 1. 지정된 영역 생성. (InteractionLengthStraight를 통한 수정 불가.)
 * const position1 = new Ditap.Cartesian3(
 *       -3044763.7129576365,
 *       4043820.120565581,
 *       3867446.689131772
 * );
 * const position2 = new Ditap.Cartesian3(
 *       -3044777.2576141134,
 *       4043794.3736621705,
 *       3867462.8381184633
 * );
 * const position3 = new Ditap.Cartesian3(
 *       -3044790.666652001,
 *        4043806.9208138464,
 *        3867439.3203376085
 * );
 * const positions = [position1, position2, position3];
 *
 * const options = {
 *  viewer: viewer,
 *  positions: positions,
 *  labelOption: "total",
 *  entityColor: Color.fromBytes(169, 67, 30, 255)
 * };
 *
 * const measurementLengthStraight = new Ditap.MeasurementLengthStraight(options);
 *
 * // 2. 마우스로 영역 지정 후 생성. (InteractionLengthStraight를 통해 직선 거리 측정 및 영역 표시.)
 * const measurementLengthStraight = new Ditap.MeasurementLengthStraight({
 *  viewer: viewer
 * });
 *
 * const interactionLengthStraight = new Ditap.InteractionLengthStraight();
 * interactionLengthStraight.measurementLengthStraight = measurementLengthStraight;
 */
class MeasurementLengthStraight {
  constructor(options) {
    const viewer = options.viewer;
    const positions = options.positions;
    const labelOption = options.labelOption;
    const entityColor = options.entityColor;

    this._id = `MeasurementLengthStraight${createGuid()}`;
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
      viewer.ditapStorage.measurementLengthStraight[this.id] = this;
    } else {
      console.error("Type doesn't match");
    }
    if (entityColor instanceof Color) {
      this._entityColor = entityColor;
    } else {
      this._entityColor = Color.fromBytes(169, 67, 30, 255);
    }
    if (labelOption === "total") {
      this._isLabelOption = labelOption;
    } else {
      this._isLabelOption = "single";
    }
    this._positions = [];
    this._centerPosition = undefined;
    this._pointEntities = [];
    this._labelEntities = [];
    this._polylineEntities = [];
    this._length = undefined;
    this._labelHeight = undefined;
    this._windowResizeEvent = undefined;
    this._cameraChangedEvent = undefined;
    this._entityNum = 0;
    this._displayCondition = 2000;

    if (positions) {
      this.positions = positions;
      this.createEntity();
    } else {
      this.positions = [];
    }
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  set viewer(viewer) {
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
    }
  }
  get viewer() {
    return this._viewer;
  }

  /*
   * 측정 객체 ID. </br>
   * ID to assign to the measurement object.
   * @type {string}
   */
  set id(id) {
    this._id = id;
  }
  get id() {
    return this._id;
  }

  /**
   * 직선 거리 측정 할 Cartesian 배열</br>
   * Cartesian array for measuring straight distances.
   * @type {Cartesian3[]}
   * @readonly
   */
  set positions(positions) {
    this._positions = positions;
  }
  get positions() {
    return this._positions;
  }

  /*
   * 폴리라인 엔티티 중심 Cartesian 값.</br>
   * Center cartesians of the polylineEntity.
   * @type {Cartesian3}
   */
  set centerPosition(centerPosition) {
    this._centerPosition = centerPosition;
  }
  get centerPosition() {
    return this._centerPosition;
  }

  /**
   * 직선 거리 측정으로 얻은 포인트 엔티티 배열.</br>
   * Array of point entities resulting from straight length measurements.
   * @type {Entity[]}
   * @readonly
   */
  set pointEntities(pointEntities) {
    this._pointEntities = pointEntities;
  }
  get pointEntities() {
    return this._pointEntities;
  }

  /**
   * 직선 거리 측정으로 얻은 폴리라인 엔티티 배열.</br>
   * Array of polyline entities resulting from straight length measurements.
   * @type {Entity[]}
   * @readonly
   */
  set polylineEntities(polylineEntities) {
    this._polylineEntities = polylineEntities;
  }
  get polylineEntities() {
    return this._polylineEntities;
  }

  /*
   * 직선 거리 측정으로 얻은 라벨 엔티티 배열.</br>
   * Array of label elements resulting from straight length measurements.
   * @type {Element[]}
   */
  set labelEntities(labelEntities) {
    this._labelEntities = labelEntities;
  }
  get labelEntities() {
    return this._labelEntities;
  }

  /**
   * 측정된 직선 거리 값.</br>
   * Measured straight length value.
   * @type {number}
   * @readonly
   */
  set length(length) {
    this._length = length;
  }
  get length() {
    return this._length;
  }

  /**
   * 직선 길이 측정 완료 후 라벨 표시 옵션. </br>
   * "total" : 폴리라인 전체 길이 표시. </br>
   * "single" : 폴리라인 개별 길이 표시. </br></br>
   * Label display option after completing the straight distance measurement.</br>
   * "total" : Labels displaying the full length of the polyline.</br>
   * "single" : Labels showing the length for each polyline.</br></br>
   *  ※ 주의사항(Note). </br>
   * 측정을 시작하기 전에 옵션 값을 변경하여야 반영. 측정이 완료된 경우 옵션 값 변경 시 반영되지 않음.</br>
   * Options must be changed before starting the measurement to take effect. Changes to options are not reflected once the measurement is completed.
   * @type {string}
   */
  set isLabelOption(isLabelOption) {
    this._isLabelOption = isLabelOption;
  }
  get isLabelOption() {
    return this._isLabelOption;
  }

  /*
   * 라벨 내부 높이. </br>
   * Internal height of the label element.
   * @type {number}
   */
  set labelHeight(labelHeight) {
    this._labelHeight = labelHeight;
  }
  get labelHeight() {
    return this._labelHeight;
  }

  /*
   * 윈도우 크기 조절 시 실행 이벤트.</br>
   * Events to run when window resizing.
   * @type {Function}
   */
  set windowResizeEvent(windowResizeEvent) {
    this._windowResizeEvent = windowResizeEvent;
  }
  get windowResizeEvent() {
    return this._windowResizeEvent;
  }

  /*
   * 카메라 시점 변경 시 실행 이벤트. </br>
   * Events to run when changing camera viewpoint in window.
   * @type {Function}
   */
  set cameraChangedEvent(cameraChangedEvent) {
    this._cameraChangedEvent = cameraChangedEvent;
  }
  get cameraChangedEvent() {
    return this._cameraChangedEvent;
  }

  /*
   * 엔티티 번호. </br>
   * Number of entities.
   * @type {number}
   */
  set entityNum(entityNum) {
    this._entityNum = entityNum;
  }
  get entityNum() {
    return this._entityNum;
  }

  /*
   * 엔티티의 DistanceDisplayCondition 값.</br>
   * DistanceDisplayCondition value of entity.
   * @type {number}
   */
  set displayCondition(displayCondition) {
    this._displayCondition = displayCondition;
  }
  get displayCondition() {
    return this._displayCondition;
  }

  /**
   * 엔티티 색상.<br>
   * Color of the entity.
   */
  set entityColor(entityColor) {
    if (entityColor instanceof Color) {
      this._entityColor = entityColor;
      Utils.changePointColor(this.pointEntities, entityColor);
      Utils.changePolylineColor(this.polylineEntities, entityColor);
    }
  }
  get entityColor() {
    return this._entityColor;
  }

  /**
   * 인스턴스 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the instance. Should be called if permanently removing the instance from layout.
   */
  destroy() {
    this.removeAllEntity();
    this.removeChangeEvent();

    // viewerStorage에서 삭제
    this.viewer.ditapStorage.removeInstanceById(this.id);

    if (this.label instanceof Element) {
      this.label.removeEventListener("mouseover", Utils.hideLabelElement);
      this.label.removeEventListener("mouseover", Utils.showLabelElement);
    }

    if (this.labelEntities && this.labelEntities.length > 0) {
      this.labelEntities.forEach((label) => {
        if (label instanceof Element) {
          label.removeEventListener("mouseover", Utils.hideLabelElement);
          label.removeEventListener("mouseover", Utils.showLabelElement);
        }
      });
    }

    this.id = undefined;
    this.positions = [];
    this.centerPosition = undefined;
    this.pointEntities = [];
    this.labelEntities = [];
    this.polylineEntities = [];
    this.length = undefined;
    this.isLabelOption = "single";
    this.labelHeight = undefined;
    this.windowResizeEvent = undefined;
    this.cameraChangedEvent = undefined;
    this.resizeObserver = undefined;
    this.entityNum = 0;
  }

  /*
   * 모든 엔티티 제거.</br>
   * Remove all entities and initialize arrays
   */
  removeAllEntity() {
    const viewer = this.viewer;
    for (const entity of this.pointEntities) {
      viewer.entities.remove(entity);
    }
    for (const entity of this.polylineEntities) {
      viewer.entities.remove(entity);
    }
    for (const label of this.labelEntities) {
      label.remove();
    }
    this.pointEntities = [];
    this.polylineEntities = [];
    this.labelEntities = [];
  }

  /*
   * 배열의 마지막 요소 제거. </br>
   * Remove last element from array.
   * @param {Entity[]} array 요소를 제거할 배열.</br> Array to remove last element.
   */
  removeLastElement(array) {
    array.pop();
  }

  /*
   * Ditap Viewer에서 엔티티 제거.</br>
   * Remove entity from viewer.
   * @param {Entity} entity 제거할 엔티티.</br> Entities to remove.
   */
  removeViewerEntity(entity) {
    this.viewer.entities.remove(entity);
  }

  /*
   * Cartesian 좌표들의 총 거리 계산.</br>
   * Calculate the total length of cartesians.
   * @param {Cartesian3[]} cartesians 총 거리를 계산하기 위한 Cartesian 배열.</br> Cartesians to calculate total length.
   * @returns {number} 계산된 총 거리.</br> The calculated total distance.
   */
  calculateLength(cartesians) {
    let length = 0;
    for (let i = 0; i < cartesians.length - 1; i++) {
      length =
        length +
        Number(
          Cartesian3.distance(cartesians[i], cartesians[i + 1]).toFixed(2)
        );
    }
    length = Number(length.toFixed(2));
    return length;
  }

  /*
   * 직선 거리 측정 후 엔티티 생성.</br>
   * Create entities after measuring straight length.
   */
  createEntity() {
    const positions = this.positions;
    const displayCondition = Utils.getDistanceDisplayConditon(positions);

    if (displayCondition > 2000) {
      this.displayCondition = displayCondition;
    }

    for (let i = 0; i < positions.length; i++) {
      const pointOPtion = {
        viewer: this.viewer,
        id: this.id + this.entityNum,
        position: positions[i],
        outlineColor: this.entityColor,
        displayCondition: this.displayCondition,
      };
      this.entityNum++;
      const point = Utils.createPoint(pointOPtion);
      this.pointEntities.push(point);
    }
    const polylineOption = {
      viewer: this.viewer,
      id: this.id + this.entityNum,
      positions: positions,
      material: this.entityColor,
      depthFaileMaterial: this.entityColor,
      displayCondition: this.displayCondition,
    };
    this.entityNum++;
    const polyline = Utils.createPolyline(polylineOption);
    this.polylineEntities.push(polyline);
    this.createLabelByOption();

    if (this.cameraChangedEvent === undefined) {
      this.createCameraMoveEvent();
    }
    if (this.windowResizeEvent === undefined) {
      this.createWindowResizeEvent();
    }
  }

  /*
   * 옵션에 따른 라벨 생성.
   * Create labels based on options.
   */
  createLabelByOption() {
    const positions = this.positions;
    const option = this.isLabelOption;
    if (option === "total") {
      // 폴리라인 총 길이 라벨
      const length = this.calculateLength(positions);
      this.length = length;

      const centerPoint = Utils.getCenterPosition(positions);
      this.centerPosition = centerPoint;

      const label = Utils.createTotalLabel(this.viewer, this.length);
      this.labelHeight = label.clientHeight;

      Utils.changeLabelLocation(
        this.viewer,
        centerPoint,
        label,
        this.labelHeight
      );
      this.labelEntities.push(label);
      this.label = label;
    } else {
      // 각각의 폴리라인 별 Label
      let length = 0;
      let totalLength = 0;
      for (let i = 0; i < positions.length; i++) {
        if (i > 0) {
          const sector = [positions[i - 1], positions[i]];
          length = this.calculateLength(sector);
          totalLength = Number((totalLength + length).toFixed(2));
        }
        this.length = totalLength;
        const label = Utils.createLabel(this.viewer, length, totalLength);
        this.labelHeight = label.clientHeight;

        Utils.changeLabelLocation(
          this.viewer,
          positions[i],
          label,
          this.labelHeight
        );
        this.labelEntities.push(label);
      }
    }
  }

  /*
   * 윈도우 크기 조절 이벤트 제거.</br>
   * Create the window resize event.
   */
  removeChangeEvent() {
    if (this.cameraChangedEvent) {
      this.cameraChangedEvent();
    }

    if (this.windowResizeEvent) {
      window.removeEventListener("resize", this.windowResizeEvent);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   */
  createWindowResizeEvent() {
    const container = this.viewer.container;

    const resizeOriginal = () => {
      this.changeLabelVisibilityByOption();
    };

    this.windowResizeEvent = () => {
      resizeOriginal();

      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.windowResizeEvent);

    // ResizeObserver 객체를 생성하고 콜백 함수 전달.
    this.resizeObserver = new ResizeObserver(resizeOriginal);

    // 대상 요소에 대한 크기 변화 관찰.
    this.resizeObserver.observe(container);
  }

  /*
   * 카메라 시점 이동 시 이벤트 생성. </br>
   * Create an event when moving the camera point of view.
   */
  createCameraMoveEvent() {
    const viewer = this.viewer;

    // 카메라 이동 이벤트 생성
    this.cameraChangedEvent = viewer.camera.changed.addEventListener(() => {
      this.changeLabelVisibilityByOption();
    });
  }

  /**
   * 라벨 옵션에 따라 라벨 가시성 변경 함수<br>
   * Change label visibility according to label options.
   */
  changeLabelVisibilityByOption() {
    const viewer = this.viewer;
    const labelEntities = this.labelEntities;
    const positions = this.positions;
    const height = this.labelHeight;
    const displayCondition = this.displayCondition;

    if (this.isLabelOption === "total") {
      Utils.changeTotalLabelVisibility(
        viewer,
        labelEntities[0],
        this.centerPosition ?? positions[0],
        displayCondition,
        height
      );
    } else {
      Utils.changeLabelVisibility(
        viewer,
        labelEntities,
        positions,
        displayCondition,
        height
      );
    }
  }
}

export default MeasurementLengthStraight;
